import { InputFieldWithLabel } from '../InputField';
import React, { useContext, useEffect } from 'react';
import s from './PosDeviceLogin.scss';
import { AppContextProvider } from '../AppLoader';
import { Box } from '@wix/design-system';
import { dataHooks } from '../../dataHooks';
import { DialogFormContainer } from '../DialogContainer';
import { keys } from '../../translationsKeys';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { ThemedHeading, ThemedButton, ThemedText } from '../ThemedComponents';
import { ButtonWithLoader } from '../ButtonWithLoader/ButtonWithLoader';
import { ROUTES } from '../../routes';

interface PosDeviceLoginProps {}

export const PosDeviceLogin: React.FC<PosDeviceLoginProps> = observer(() => {
  const {
    rootStore: {
      posDeviceLoginStore,
      navigationStore: { curryNavigate: navigate },
      displayStore: { isMobile },
    },
  } = useContext(AppContextProvider);

  useEffect(() => {
    posDeviceLoginStore.reportPageOpened();
  }, []);

  const { t } = useTranslation();
  const subtitle_font_size = 'medium';
  const email_font_size = 'small';
  return (
    <Box direction="vertical" width="100%" align="center">
      <DialogFormContainer
        dataHook={dataHooks.posDeviceLogin.container}
        formClasses={[s.posDeviceLoginConnect]}
        mainHeaderProps={{ showBackButton: false }}
      >
        <ThemedHeading
          appearance={isMobile ? 'H2' : 'H1'}
          dataHook={dataHooks.posDeviceLogin.title}
        >
          {t(keys['posDevice.login.connect.code.title'])}
        </ThemedHeading>
        <Box
          marginBottom="40px"
          marginTop={1}
          textAlign="center"
          display="grid"
          width="100%"
        >
          <ThemedText size={subtitle_font_size}>
            {t(keys['posDevice.login.connect.code.content.first'])}
          </ThemedText>
          <ThemedText size={subtitle_font_size}>
            {t(keys['posDevice.login.connect.code.content.second'])}
          </ThemedText>
        </Box>
        <Box direction="vertical" align="center" className={s.codeInput}>
          <InputFieldWithLabel
            required
            formField={posDeviceLoginStore.code}
            label={t(keys['posDevice.login.connect.code.label'])}
            type="text"
            marginBottom={0}
            wrapperClassName={s.emailInput}
            dataHook={dataHooks.posDeviceLogin.codeInput}
            autoFocus
            onChange={(e) => {
              posDeviceLoginStore.code.setValue(e.target.value.toUpperCase());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                posDeviceLoginStore.submit();
              }
            }}
          />
        </Box>
        <Box marginBottom={0} direction="vertical" verticalAlign="middle">
          <Trans
            i18nKey={posDeviceLoginStore.contentKey}
            values={{
              currentLoggedInEmail: posDeviceLoginStore?.currentLoggedInEmail,
            }}
          >
            <ThemedText size={email_font_size}>
              You’re currently logged in as
            </ThemedText>
            <ThemedText size={email_font_size} weight="bold">
              email
            </ThemedText>
          </Trans>
        </Box>
        <Box verticalAlign="middle" paddingTop="36px">
          <Box
            gap="SP2"
            verticalAlign="middle"
            display={isMobile ? 'grid' : 'flex'}
            width="100%"
          >
            <ThemedButton
              size="large"
              onClick={() => {
                posDeviceLoginStore.reportButtonClicked(
                  dataHooks.posDeviceLogin.anotherAccountCta,
                );
                navigate(
                  `/?targetUrl=${window.location.origin}/login${ROUTES.POS_DEVICE_LOGIN}`,
                )();
              }}
              priority="secondary"
              skin="standard"
              dataHook={dataHooks.posDeviceLogin.anotherAccountCta}
            >
              {t(keys['posDevice.login.connect.another.account.button'])}
            </ThemedButton>
            <ButtonWithLoader
              className={s.connectButton}
              disabled={!posDeviceLoginStore.code.value.length}
              onClick={() => {
                posDeviceLoginStore.reportButtonClicked(
                  dataHooks.posDeviceLogin.connect,
                );
                void posDeviceLoginStore.submit();
              }}
              dataHook={dataHooks.posDeviceLogin.connect}
              skin="standard"
            >
              {t(keys['posDevice.login.connect.button'])}
            </ButtonWithLoader>
          </Box>
        </Box>
      </DialogFormContainer>
    </Box>
  );
});
